import { inject, Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, catchError, combineLatest, filter, forkJoin, map, Observable, of, Subscription, switchMap } from "rxjs";
import { safeParseFloat } from "../utils/common";
import { BetPlacementService } from "./bet-placement.service";
import { ExposureService } from "./exposure.service";
import { formatNumber } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { LineMarketPnlComponent } from "../../../../desktop/src/app/pages/content/market-layout/market/line-market-pnl/line-market-pnl.component";
import { BalanceService } from "./balance.service";
import { CalculationBet, MarketProvider, PredictedBet } from "../types/bet.type";
import { BetService } from "./bet.service";

@Injectable({
  providedIn: "root",
})
export class PnlCalculatorService implements OnDestroy {
  subscriptions: Subscription = new Subscription();
  betPlacementService = inject(BetPlacementService);
  exposureService = inject(ExposureService);
  bets$: any = new BehaviorSubject<any>([]);
  currentPlacement$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  balanceService = inject(BalanceService);
  betService = inject(BetService);
  readonly dialog = inject(MatDialog);

  constructor() {
    this.getOpenBetUpdates();
    this.getCurrentPlacement();
  }

  showLinePnl(marketId: string, type: "fancy" | "normal" = "normal") {
    this.dialog.open(LineMarketPnlComponent, {
      data: {
        marketId,
        type,
      },
      width: "25%",
      exitAnimationDuration: 0,
    });
  }

  getCurrentPlacement() {
    this.betPlacementService.getCurrentPlacement().subscribe((value: any) => {
      this.exposureService.clearPredictedMarketExposure();
      this.currentPlacement$.next(value || null);
    });
  }

  getOpenBetUpdates() {
    this.betPlacementService.getOpenBetsObservable().subscribe((data) => {
      // console.log(data);
      if (data) {
        this.bets$.next(data);
      }
    });
  }

  getNetEffect(marketId: string, selectionId: number, matchedOnly: boolean = true, options?: any): Observable<number | null> {
    return this.bets$.pipe(
      map((bets: any[]) => {
        const isDoubleChance = options?.isDoubleChance;
        const filteredBets = bets.filter((bet: any) => {
          const provider = bet?.provider;
          const isSb = provider === "SB";
          const isOddsMarket = bet?.bettingType === "ODDS";
          const betMarketId = isSb && isOddsMarket ? bet?.topic : bet?.marketId;
          const betSelectionId = bet?.selectionId;

          const marketMatches = marketId === betMarketId;
          const isMatched = safeParseFloat(bet?.stakeRemaining) === 0 && safeParseFloat(bet?.stakeMatched) > 0;
          const marketMatchesAndIsMatched = marketMatches && isMatched;
          const selectionMatches = selectionId === betSelectionId;

          if (matchedOnly) {
            return marketMatchesAndIsMatched;
          }

          if (isDoubleChance) {
            return marketMatchesAndIsMatched && selectionMatches;
          }

          return marketMatches;
        });

        // Double chance P/L distribution over a single selection
        if (isDoubleChance) {
          const { profit, loss } = filteredBets.reduce(
            (acc: any, item: any) => {
              if (item.selectionId === selectionId) {
                if (item.side === "BACK") {
                  acc.profit += safeParseFloat(item.potentialProfit);
                  acc.loss += -safeParseFloat(item.potentialLoss);
                } else if (item.side === "LAY") {
                  acc.profit += -safeParseFloat(item.potentialLoss);
                  acc.loss += safeParseFloat(item.potentialProfit);
                }
              }
              return acc;
            },
            { profit: 0, loss: 0 },
          );

          if (profit === 0 && loss === 0) return null;

          return { profit, loss };
        }

        // Normal P/L distribution over selections
        return filteredBets.reduce((acc: any, bet: any) => {
          acc = acc === null ? 0 : acc;

          if (bet.selectionId === selectionId) {
            // Effect of the bet on the runner itself
            const betEffect = bet.side === "BACK" ? safeParseFloat(bet.potentialProfit) : -safeParseFloat(bet.potentialLoss);
            return acc + betEffect;
          } else {
            // Effect on other runners: assuming losing the stake for BACK and gaining stake for LAY on others
            const otherEffect = bet.side === "BACK" ? -safeParseFloat(bet.potentialLoss) : safeParseFloat(bet.potentialProfit);
            return acc + otherEffect;
          }
        }, null);
      }),
    );
  }

  getPotentialNetEffect(marketId: string, selectionId: number): Observable<any> {
    return this.currentPlacement$.pipe(
      switchMap((placement: any): any => {
        const isDoubleChance = placement?.data?.isDoubleChance;
        const placements = Array.isArray(placement) ? placement : placement ? [placement] : [];

        const _placements = placements
          .filter((p) => {
            const pData = p?.data;

            if (pData?.hasOwnProperty("topic")) return pData?.topic === marketId;

            const marketMatches = pData?.MARKET_ID === marketId;
            const selectionMatches = pData?.SELECTION_ID === selectionId;

            if (isDoubleChance) return marketMatches && selectionMatches;

            return marketMatches;
          })
          .map((each) => {
            const data = each?.data;
            const price = safeParseFloat(data?.F_ODDS);
            const stake = safeParseFloat(data?.stake);
            const isBack = data?.SIDE === "BACK";
            const potentialProfit = isBack ? (price - 1) * stake : stake;
            const potentialLoss = isBack ? stake : (price - 1) * stake;

            return {
              ...data,
              marketId: data?.MARKET_ID,
              selectionId: data?.SELECTION_ID,
              side: data?.SIDE,
              potentialProfit,
              potentialLoss,
            };
          });

        if (!_placements?.length) {
          return of(null);
        }

        return this.bets$.pipe(
          map((bets: any[]) => {
            const filteredBets = bets.filter((bet: any) => {
              const provider = bet?.provider;
              const isSb = provider === "SB";
              const isOddsMarket = bet?.bettingType === "ODDS";
              const betMarketId = isSb && isOddsMarket ? bet?.topic : bet?.marketId;
              const betSelectionId = bet?.selectionId;

              const isMatched = safeParseFloat(bet?.stakeRemaining) === 0 && safeParseFloat(bet?.stakeMatched) > 0;
              const marketMatchesAndIsMatched = marketId === betMarketId && isMatched;
              const selectionMatches = selectionId === betSelectionId;

              if (isDoubleChance) {
                return marketMatchesAndIsMatched && selectionMatches;
              }

              return marketMatchesAndIsMatched;
            });

            // Merging _placements with bets
            const mergedData = [..._placements, ...filteredBets];

            // Double chance P/L distribution over a single selection
            if (isDoubleChance) {
              const { profit, loss } = mergedData.reduce(
                (acc: any, item: any) => {
                  if (item.selectionId === selectionId) {
                    if (item.side === "BACK") {
                      acc.profit += safeParseFloat(item.potentialProfit);
                      acc.loss += -safeParseFloat(item.potentialLoss);
                    } else if (item.side === "LAY") {
                      acc.profit += -safeParseFloat(item.potentialLoss);
                      acc.loss += safeParseFloat(item.potentialProfit);
                    }
                  }
                  return acc;
                },
                { profit: 0, loss: 0 },
              );

              return { profit, loss };
            }

            // Normal P/L distribution over selections
            return mergedData.reduce((acc: any, item: any) => {
              acc = acc === null ? 0 : acc;

              if (item.selectionId === selectionId) {
                // Effect of the item on the runner itself
                const itemEffect = item.side === "BACK" ? safeParseFloat(item.potentialProfit) : -safeParseFloat(item.potentialLoss);
                return acc + itemEffect;
              } else {
                // Effect on other runners: assuming losing the stake for BACK and gaining stake for LAY on others
                const otherEffect = item.side === "BACK" ? -safeParseFloat(item.potentialLoss) : safeParseFloat(item.potentialProfit);
                return acc + otherEffect;
              }
            }, null);
          }),
        );
      }),
    );
  }

  getLineNetEffect(marketId: string): Observable<any> {
    // if (this.currentPlacement$.value) return of(null);

    return this.bets$.pipe(
      filter((bets: any[]) => bets && bets.length > 0),
      map((bets: any[]) =>
        bets.filter((bet: any) => {
          const descPrefix = bet?.descriptionPrefix;
          const betMarketId = descPrefix === "Fancy" ? bet?.selectionId : bet?.marketId;

          return betMarketId === marketId;
        }),
      ),
      map((filteredBets: any[]) => {
        const isFancy = filteredBets?.some((item) => item?.descriptionPrefix === "Fancy");
        const marketBets = filteredBets.map((bet) => bet.averagePriceMatched);

        if (marketBets?.length === 0) {
          return of(null);
        }

        const { lowestRun, highestRun, isTwoRanges, headers } = this.rangeDetails(marketBets, isFancy);

        // Assuming calculateRanges is a pure function returning ranges
        const ranges = this.calculateRanges(filteredBets, lowestRun, highestRun);

        const rows = isTwoRanges ? [ranges.range1, ranges.range3] : [ranges.range1, ranges.range2, ranges.range3];

        return {
          headers,
          rows,
          maxExposure: Math.min(...rows),
        };
      }),
    );
  }

  getLinePotentialNetEffect(marketId: string): Observable<any> {
    return this.currentPlacement$.pipe(
      switchMap((placement) => {
        const { data } = placement || {};
        const isFancy = data?.descriptionPrefix === "Fancy";
        const mId = isFancy ? data?.SELECTION_ID : data?.MARKET_ID;

        // console.log(data);

        if (mId !== marketId || !placement) {
          return of(null);
        }

        return this.bets$.pipe(
          map((bets: any[]) =>
            bets.filter((bet: any) => {
              const descPrefix = bet?.descriptionPrefix;
              const betMarketId = descPrefix === "Fancy" ? bet?.selectionId : bet?.marketId;

              return betMarketId === marketId;
            }),
          ),
          map((filteredBets: any[]) => {
            if (!data) return null;

            const marketBets = filteredBets.map((item) => item.averagePriceMatched);
            const allPrices = [...marketBets, data?.price];
            const { lowestRun, highestRun, isTwoRanges, headers } = this.rangeDetails(allPrices, isFancy);

            // Calculate initial ranges
            const ranges = this.calculateRanges(filteredBets, lowestRun, highestRun);

            // Process placement stake and runs
            const placementStake = safeParseFloat(data?.stake);
            const runs = data?.SIDE === "BACK" ? Math.floor(data?.price) : Math.ceil(data?.price);

            // Apply placement range updates
            const updatedRanges = this.placementRanges(ranges, placementStake, lowestRun, highestRun, runs, data?.SIDE, data?.descriptionPrefix, data?.F_ODDS);

            const rows = isTwoRanges ? [updatedRanges.range1, updatedRanges.range3] : [updatedRanges.range1, updatedRanges.range2, updatedRanges.range3];

            // Return final headers and rows
            return {
              headers,
              rows,
              maxExposure: Math.min(...rows),
            };
          }),
          catchError((err) => {
            console.error("Error calculating potential net effects:", err);
            return of(null); // Provide a default output on error
          }),
        );
      }),
    );
  }

  private rangeDetails(bets: number[], isFancy?: boolean) {
    const minPrice = Math.min(...bets);
    const maxPrice = Math.max(...bets);
    const lowestRun = isFancy ? minPrice - 1 : Math.floor(minPrice);
    const highestRun = Math.ceil(maxPrice);
    const isTwoRanges = bets.length === 1 || highestRun - lowestRun === 1;
    const headers = isTwoRanges
      ? [lowestRun > 0 ? `0-${lowestRun}` : "0", `${highestRun}+`]
      : [`0-${lowestRun}`, lowestRun + 1 === highestRun - 1 ? lowestRun + 1 : `${lowestRun + 1}-${highestRun - 1}`, `${highestRun}+`];
    return { lowestRun, highestRun, isTwoRanges, headers };
  }

  private calculateRanges(bets: any[], lowestRun: number, highestRun: number): any {
    const initialRanges = { range1: 0, range2: 0, range3: 0 };

    return bets.reduce((acc, item) => {
      const stake = safeParseFloat(item?.betSize);
      const runs = item?.side === "BACK" ? Math.floor(item?.averagePriceMatched) : Math.ceil(item?.averagePriceMatched);
      return this.rangeUpdates(acc, stake, runs, lowestRun, highestRun, item?.side, item?.descriptionPrefix, item?.fOdds);
    }, initialRanges);
  }

  private placementRanges(
    ranges: any,
    stake: number,
    lowestRun: number,
    highestRun: number,
    runs: number,
    side: "BACK" | "LAY",
    descPrefix?: string,
    fOdds?: string,
  ): any {
    // Create a copy of the ranges object to avoid direct mutation
    const updatedRanges = { ...ranges };

    // Apply the updates on the copied object
    return this.rangeUpdates(updatedRanges, stake, runs, lowestRun, highestRun, side, descPrefix, fOdds);
  }

  private rangeUpdates(
    ranges: any,
    stake: number,
    runs: number,
    lowestRun: number,
    highestRun: number,
    side: "BACK" | "LAY",
    descPrefix?: string,
    fOdds?: string,
  ): any {
    // Create a copy of ranges to avoid mutation
    const updatedRanges = { ...ranges };
    const isBack = side === "BACK";
    const isLay = side === "LAY";
    const isFancy = descPrefix === "Fancy";
    const fancyProfit = isBack ? (safeParseFloat(fOdds) - 1) * stake : -((safeParseFloat(fOdds) - 1) * stake);
    const fancyLiability = isBack ? -stake : stake;
    const profit = isFancy ? fancyProfit : (safeParseFloat(fOdds) - 1) * stake;
    const liability = isFancy ? fancyLiability : -stake;

    if (runs >= 0 && runs <= lowestRun) {
      updatedRanges.range1 += profit;
      updatedRanges.range2 += liability;
      updatedRanges.range3 += liability;
    }

    if (runs > lowestRun && runs < highestRun) {
      updatedRanges.range1 += isBack ? profit : liability;
      updatedRanges.range2 += profit;
      updatedRanges.range3 += isLay ? profit : liability;
    }

    if (runs >= highestRun) {
      updatedRanges.range1 += liability;
      updatedRanges.range2 += liability;
      updatedRanges.range3 += profit;
    }

    // Return the new, updated ranges object
    return updatedRanges;
  }

  getLadderTable(marketId: string, type: "normal" | "fancy" = "normal", returnType: "ladder" | "exposure" = "ladder") {
    return combineLatest([this.bets$, this.currentPlacement$]).pipe(
      map(([bets, currentPlacement]: any) => {
        // new placement transformed into the structure of existing bets
        const convertedPlacement = currentPlacement?.data
          ? {
              side: currentPlacement?.data?.SIDE,
              selectionId: currentPlacement?.data?.SELECTION_ID,
              marketId: currentPlacement?.data?.MARKET_ID,
              averagePriceMatched: currentPlacement?.data?.price,
              betSize: currentPlacement?.data?.stake,
              fOdds: currentPlacement?.data?.F_ODDS,
            }
          : null;
        // combine the new ongoing placement with the existing bets
        const combinedBets = convertedPlacement ? [...bets, convertedPlacement] : bets;
        const filteredBets = combinedBets.filter((bet: any) => {
          const isFancy = type === "fancy";
          const betMarketId = isFancy ? bet?.selectionId : bet?.marketId;
          return betMarketId === marketId && safeParseFloat(bet?.stakeRemaining) === 0;
        });
        return {
          filteredBets,
          convertedPlacement,
        };
      }),
      map(({ filteredBets, convertedPlacement }: { filteredBets: any[]; convertedPlacement: any }) => {
        const isNormal = type === "normal";
        if (filteredBets.length === 0) return;
        const runs = filteredBets.map((item) => (isNormal ? Math.floor(safeParseFloat(item.averagePriceMatched)) : safeParseFloat(item.averagePriceMatched)));
        let minRun = Math.min(...runs);
        let maxRun = Math.max(...runs);

        const tableRuns = Array.from({ length: maxRun + 2 - (minRun - 2) + 1 }, (_, i) => ({
          runs: minRun - 2 + i,
          pnl: 0,
        }));

        tableRuns.forEach((runItem: { runs: number; pnl: number }) => {
          filteredBets.forEach((bet) => {
            const fOdds = safeParseFloat(bet.fOdds);
            const price = safeParseFloat(bet.averagePriceMatched);
            const stake = safeParseFloat(bet.betSize);

            // BACK means UNDER (Runs will be UNDER some run value)
            // LAY means OVER (Runs will be OVER some run value)
            // BACK means YES (Runs will be >= some run value)
            // LAY means NO (Runs will be < some run value)
            if (bet.side === "LAY") {
              // OVER for BF Line Markets
              // NO for Fancy Markets
              if (runItem.runs < price) {
                isNormal ? (runItem.pnl -= stake) : (runItem.pnl += stake);
              } else {
                isNormal ? (runItem.pnl += stake) : (runItem.pnl -= (fOdds - 1) * stake);
              }
            } else if (bet.side === "BACK") {
              // UNDER for BF Markets
              // YES for Fancy Markets
              if (runItem.runs < price) {
                isNormal ? (runItem.pnl += stake) : (runItem.pnl -= stake);
              } else {
                isNormal ? (runItem.pnl -= stake) : (runItem.pnl += (fOdds - 1) * stake);
              }
            }
          });
        });

        if (returnType === "exposure") {
          const pnlValues = tableRuns.map((each) => each.pnl);
          return {
            type: convertedPlacement?.[isNormal ? "marketId" : "selectionId"] === marketId ? "new" : "active",
            value: Math.min(...pnlValues),
          };
        }

        return tableRuns;
      }),
    );
  }

  hasPlacedBets(marketId: any, type: "normal" | "fancy" = "normal"): Observable<boolean> {
    return combineLatest([this.bets$, this.currentPlacement$]).pipe(
      map(([bets, placement]: any) => {
        const isNormal = type === "normal";
        const hasBet =
          bets?.some((bet: any) => {
            const mId = isNormal ? bet?.marketId : bet?.selectionId;
            const isMatched = safeParseFloat(bet?.stakeRemaining) === 0 && safeParseFloat(bet?.stakeMatched) > 0;
            return mId === marketId && isMatched;
          }) || false;
        const placementId = isNormal ? placement?.data?.MARKET_ID : placement?.data?.SELECTION_ID;
        const hasSelection = placementId === marketId;

        return hasBet || hasSelection;
      }),
    );
  }

  getCashOutOffer(market: any, marketOdds: any): Observable<any> {
    // console.log(market);
    let marketRunners: any;
    if (market?.runners) {
      marketRunners = new Map(market?.runners.map((runner: any) => [runner.selectionId, runner]));
    }
    return this.bets$.pipe(
      // Filter bets for the specific marketId
      map((bets: any[]) => bets.filter((bet: any) => bet.marketId === market?.marketId && safeParseFloat(bet?.stakeRemaining) === 0)),
      switchMap((betsForMarket: any[]) => {
        // console.log("BetsForMarket -> ", betsForMarket);
        if (!betsForMarket.length) {
          return of(null); // No bets, no cash-out
        }

        const currentProfitLoss$: any[] = marketOdds.map((runner: any) =>
          of({
            selectionId: runner.id,
            handicap: marketRunners.get(runner.id)?.handicap,
            currentPnL: this.currentProfitLoss(betsForMarket, runner.id),
            bestBackPrice: runner.ex?.abp?.[0] || null, // Best Back price
            bestLayPrice: runner.ex?.alp?.[0] || null, // Best Lay price
          }),
        );

        // Combine net effects for all runners
        return forkJoin(currentProfitLoss$).pipe(
          map((runnerPnLs: any[]) => {
            // console.log("net effects -> ", runnerPnLs);
            const minPnL = runnerPnLs.length ? Math.min(...runnerPnLs.map((r: any) => r?.currentPnL)) : 0; // Default value

            // console.log("Min PnL", minPnL);

            const cashOutBets: any[] = [];
            let runnerStakeSum: number = 0;

            runnerPnLs.forEach((runner: any) => {
              // console.log(runner);
              const ownPnL = runner?.currentPnL;
              const pnlDifference = minPnL - ownPnL;

              // console.log(
              //   "Selection Id =",
              //   runner?.selectionId,
              //   " Runner =",
              //   marketRunners.get(runner?.selectionId)?.runnerName,
              //   " Current PnL =",
              //   runner?.currentPnL,
              // );

              // console.log(
              //   "==================================== Min pnl =",
              //   minPnL,
              //   "Own pnl =",
              //   ownPnL,
              //   "pnl difference =>",
              //   pnlDifference,
              // );
              //
              // console.log("pnl difference", pnlDifference);

              // Determine the appropriate price (Lay for negative PnL diff, Back for positive)
              const oppositeOdd = pnlDifference < 0 ? runner.bestLayPrice : runner.bestBackPrice;

              // console.log("opposite odd: ", oppositeOdd);
              const stake = oppositeOdd ? pnlDifference / oppositeOdd : pnlDifference;
              // console.log("stake = ", stake);
              runnerStakeSum += stake;

              if (oppositeOdd && oppositeOdd !== 0) {
                // console.log("Found opposite odd", stake);
                // Add the bet if the stake is non-zero
                const _stake = formatNumber(stake, "en", "1.0-2");
                // console.log(Math.abs(safeParseFloat(_stake)));
                if (Math.abs(safeParseFloat(_stake)) > 0) {
                  cashOutBets.push({
                    PROVIDER: "BF",
                    PROVIDER_ID: "0",
                    SERVICE_ID: "0",
                    PRICE: oppositeOdd,
                    SIZE: Math.abs(stake),
                    SIDE: pnlDifference < 0 ? "LAY" : "BACK",
                    MARKET_ID: market?.marketId,
                    MARKET_NAME: market?.marketName,
                    SPORT_ID: market?.eventType?.id,
                    EVENT_ID: market?.event?.id,
                    PERSISTENCE_TYPE: "PERSIST",
                    SELECTION_ID: runner.selectionId,
                    HANDICAP: runner?.bet?.handicap || 0,
                    BETTING_TYPE: market?.description?.bettingType,
                    F_ODDS: oppositeOdd,
                  });
                }
              }
            });

            // console.log("Stake sum -> ", runnerStakeSum);
            // console.log("CashOut Bets -> ", cashOutBets);

            // Calculate the cash-out profit
            const cashOutProfit = minPnL - runnerStakeSum;

            // Liability can be calculated if required (adjust logic if necessary)
            const liability = Math.abs(minPnL);

            // Validate the predicted exposure for the bets that will be placed when cashing out
            const balance = this.balanceService.balanceData$.getValue();
            const cashOutBetsValidations: ({
              insufficientBalance: boolean;
            } | null)[] = [];

            if (balance) {
              cashOutBets.forEach((bet, index, cashOutBets) => {
                //TODO: confirm that the bet contains the `MARKET_NAME` attribute

                // Generate a predicted bet for the current iteration cash out bet
                const predictedBet: PredictedBet = {
                  marketGroupId: bet.MARKET_ID,
                  marketId: bet.MARKET_ID,
                  marketName: bet.MARKET_NAME,
                  selectionId: bet.SELECTION_ID,
                  side: bet.SIDE,
                  fOdds: this.betService.getFinalOdds(bet.PRICE, MarketProvider.BF, false),
                  stake: bet.SIZE,
                  bettingType: bet.BETTING_TYPE,
                  provider: MarketProvider.BF,
                  handicap: bet.HANDICAP,
                  averagePriceMatched: this.betService.getFinalOdds(bet.PRICE, MarketProvider.BF, false),
                };

                // Generate predicted bets for previous iteration's cash-out bets
                const previousCashOutBets: PredictedBet[] = cashOutBets.slice(0, index).map((cashOutBet) => ({
                  marketGroupId: cashOutBet.MARKET_ID,
                  marketId: cashOutBet.MARKET_ID,
                  marketName: cashOutBet.MARKET_NAME,
                  selectionId: cashOutBet.SELECTION_ID,
                  side: cashOutBet.SIDE,
                  fOdds: this.betService.getFinalOdds(cashOutBet.PRICE, MarketProvider.BF, false),
                  stake: cashOutBet.SIZE,
                  bettingType: cashOutBet.BETTING_TYPE,
                  provider: MarketProvider.BF,
                  handicap: cashOutBet.HANDICAP,
                  averagePriceMatched: this.betService.getFinalOdds(cashOutBet.PRICE, MarketProvider.BF, false),
                }));

                // Combine current user orders with the previous cash-out bets
                const combinedBets: CalculationBet[] = [
                  ...betsForMarket.map((bet) => ({
                    ...bet,
                    marketGroupId: this.betService.generateMarketGroupIdForCalculationBet(bet),
                    rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
                  })),
                  ...previousCashOutBets.map((previousCashOutBet) => this.betService.enhancePredictedBetToCalculationBet(previousCashOutBet)),
                ];

                // Evaluate the exposure calculation validation for each iteration and push the result into a variable to be checked later
                // const validationResult = this.betService.evaluateExposureValidation(combinedBets, balance, predictedBet, "-1", false);
                // cashOutBetsValidations.push(validationResult);
              });
            }

            // Calculate if the button should be enabled or not
            const disableCashOutButton = cashOutBetsValidations.some((validation) => validation !== null);

            // Return the required data for the UI
            return {
              cashOutBets, // Array of bets to place
              cashOutAmount: Math.abs(runnerStakeSum), // Total stake
              cashOutProfit: cashOutProfit, // Profit after cash-out
              liability, // Liability value
              minPnL,
              disableCashOutButton,
            };
          }),
        );
      }),
    );
  }

  currentProfitLoss(bets: any[], selectionId: any) {
    return bets.reduce((currentPnL: number, bet: any) => {
      if (bet.selectionId === selectionId) {
        // Effect of the bet on the runner itself
        const betEffect = bet.side === "BACK" ? safeParseFloat(bet.potentialProfit) : -safeParseFloat(bet.potentialLoss);
        return currentPnL + betEffect;
      } else {
        // Effect of the bet on other runners
        const otherEffect = bet.side === "BACK" ? -safeParseFloat(bet.potentialLoss) : safeParseFloat(bet.potentialProfit);
        return currentPnL + otherEffect;
      }
    }, 0);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
